import React, { useEffect, useState } from 'react';
import { TrendingUp } from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, BarChart, Bar
} from 'recharts';
import api from '../../services/api';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #F0DCC8', borderRadius: 10, padding: '10px 14px', boxShadow: '0 4px 16px rgba(200,75,15,0.10)', fontSize: 13 }}>
      <div style={{ fontWeight: 700, color: '#1C0A00', marginBottom: 6 }}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ color: p.color, display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: p.color }} />
          {p.name}: <strong>{p.value}</strong>
        </div>
      ))}
    </div>
  );
};

export default function AdoptionPage() {
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [groupBy, setGroupBy]   = useState('week');
  const [chartType, setChartType] = useState('area'); // 'area' | 'bar'

  const load = (gb = groupBy) => {
    setLoading(true);
    api.get(`/school-portal/adoption?groupBy=${gb}`)
      .then(r => setData(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleGroupBy = (val) => { setGroupBy(val); load(val); };

  const formatPeriod = (period) => {
    if (!period) return '';
    if (groupBy === 'month') {
      const [y, m] = period.split('-');
      return new Date(y, m - 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
    }
    if (groupBy === 'day') {
      return new Date(period).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    }
    // week: YYYY-WW
    return `Wk ${period.split('-')[1]}, ${period.split('-')[0]}`;
  };

  const chartData = data.map(d => ({
    ...d,
    period: formatPeriod(d.period),
    'Login 1': d.login1,
    'Login 2': d.login2,
  }));

  const totalLogin1 = data.reduce((s, d) => s + Number(d.login1), 0);
  const totalLogin2 = data.reduce((s, d) => s + Number(d.login2), 0);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <TrendingUp size={22} color="#C84B0F" />
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1C0A00', margin: 0 }}>Adoption Trends</h1>
      </div>
      <p style={{ fontSize: 13, color: '#9CA3AF', marginBottom: 22 }}>
        Login and redemption activity over time · Timestamps recorded post-migration only
      </p>

      {/* Controls */}
      <div className="card" style={{ padding: '14px 18px', marginBottom: 20, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 10 }}>Group by</span>
          {['day', 'week', 'month'].map(opt => (
            <button key={opt} onClick={() => handleGroupBy(opt)}
              style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${groupBy === opt ? '#C84B0F' : '#F0DCC8'}`, background: groupBy === opt ? '#C84B0F' : '#fff', color: groupBy === opt ? '#fff' : '#6B4422', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginRight: 6, textTransform: 'capitalize', transition: 'all 0.13s' }}>
              {opt}
            </button>
          ))}
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 10 }}>Chart type</span>
          {[{ val: 'area', label: 'Area' }, { val: 'bar', label: 'Bar' }].map(opt => (
            <button key={opt.val} onClick={() => setChartType(opt.val)}
              style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${chartType === opt.val ? '#C84B0F' : '#F0DCC8'}`, background: chartType === opt.val ? '#C84B0F' : '#fff', color: chartType === opt.val ? '#fff' : '#6B4422', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginRight: 6, transition: 'all 0.13s' }}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary chips */}
      {!loading && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
          <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: '10px 16px', fontSize: 13 }}>
            <span style={{ color: '#9CA3AF' }}>Total Login 1</span>
            <span style={{ fontWeight: 800, color: '#15803D', fontSize: 18, marginLeft: 10, fontVariantNumeric: 'tabular-nums' }}>{totalLogin1.toLocaleString('en-IN')}</span>
          </div>
          <div style={{ background: '#F0FDFA', border: '1px solid #99F6E4', borderRadius: 10, padding: '10px 16px', fontSize: 13 }}>
            <span style={{ color: '#9CA3AF' }}>Total Login 2</span>
            <span style={{ fontWeight: 800, color: '#0F766E', fontSize: 18, marginLeft: 10, fontVariantNumeric: 'tabular-nums' }}>{totalLogin2.toLocaleString('en-IN')}</span>
          </div>
          <div style={{ background: '#FEF3EE', border: '1px solid #FDCFB5', borderRadius: 10, padding: '10px 16px', fontSize: 13 }}>
            <span style={{ color: '#9CA3AF' }}>Data points</span>
            <span style={{ fontWeight: 800, color: '#C84B0F', fontSize: 18, marginLeft: 10 }}>{data.length}</span>
          </div>
        </div>
      )}

      {/* Chart */}
      <div className="card" style={{ padding: '20px 16px' }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, gap: 12, color: '#9CA3AF' }}>
            <div className="loader" /> Loading chart…
          </div>
        ) : data.length === 0 ? (
          <div style={{ textAlign: 'center', height: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF' }}>
            <TrendingUp size={40} color="#F0DCC8" style={{ marginBottom: 14 }} />
            <div style={{ fontSize: 15, fontWeight: 600, color: '#6B4422', marginBottom: 6 }}>No adoption data yet</div>
            <div style={{ fontSize: 13 }}>Timestamps are recorded only for codes updated after the DB migration.</div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={340}>
            {chartType === 'area' ? (
              <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="l1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#22C55E" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#22C55E" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="l2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#14B8A6" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#14B8A6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0DCC8" />
                <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#9CA3AF' }} tickLine={false} axisLine={{ stroke: '#F0DCC8' }} />
                <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />
                <Area type="monotone" dataKey="Login 1" stroke="#22C55E" strokeWidth={2.5} fill="url(#l1)" dot={{ r: 3, fill: '#22C55E', strokeWidth: 0 }} activeDot={{ r: 5 }} />
                <Area type="monotone" dataKey="Login 2" stroke="#14B8A6" strokeWidth={2.5} fill="url(#l2)" dot={{ r: 3, fill: '#14B8A6', strokeWidth: 0 }} activeDot={{ r: 5 }} />
              </AreaChart>
            ) : (
              <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0DCC8" />
                <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#9CA3AF' }} tickLine={false} axisLine={{ stroke: '#F0DCC8' }} />
                <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />
                <Bar dataKey="Login 1" fill="#22C55E" radius={[4, 4, 0, 0]} maxBarSize={40} />
                <Bar dataKey="Login 2" fill="#14B8A6" radius={[4, 4, 0, 0]} maxBarSize={40} />
              </BarChart>
            )}
          </ResponsiveContainer>
        )}
      </div>

      <div style={{ marginTop: 14, padding: '10px 14px', background: '#FEF3EE', border: '1px solid #FDCFB5', borderRadius: 10, fontSize: 12, color: '#9CA3AF' }}>
        ℹ️ Adoption data only includes codes updated <em>after</em> the database migration that added timestamp columns. Older pre-migration redemptions are not reflected in this chart.
      </div>
    </div>
  );
}
